import { useState, useEffect, useCallback } from 'react';
import { clearSession } from '../utils/session';

// Hook personalizado para manejar autenticación
export const useAuth = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Función para verificar si el token está próximo a expirar
    const isTokenExpiringSoon = useCallback(() => {
        const token = localStorage.getItem('token');
        if (!token) return true;

        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const now = Date.now() / 1000;
            const timeUntilExpiry = payload.exp - now;
            
            // Considerar que expira pronto si queda menos de 5 minutos
            return timeUntilExpiry < 300;
        } catch (error) {
            console.error('Error al verificar expiración del token:', error);
            return true;
        }
    }, []);

    // Función para refrescar el token
    const refreshToken = useCallback(async () => {
        try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (!refreshToken) {
                throw new Error('No hay refresh token disponible');
            }

            const response = await fetch(`${process.env.REACT_APP_API_URL}/refresh-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${refreshToken}`
                }
            });

            if (response.ok) {
                const { token, refreshToken: newRefreshToken } = await response.json();
                
                localStorage.setItem('token', token);
                if (newRefreshToken) {
                    localStorage.setItem('refreshToken', newRefreshToken);
                }
                
                setIsAuthenticated(true);
                return token;
            } else {
                throw new Error('Fallo al refrescar token');
            }
        } catch (error) {
            console.error('Error al refrescar token:', error);
            logout();
            return null;
        }
    }, []);

    // Función para hacer logout
    const logout = useCallback(() => {
        clearSession();
        setIsAuthenticated(false);
        setUser(null);
        window.dispatchEvent(new Event('authChange'));
    }, []);

    // Función para hacer login
    const login = useCallback((token, refreshToken) => {
        localStorage.setItem('token', token);
        if (refreshToken) {
            localStorage.setItem('refreshToken', refreshToken);
        }
        setIsAuthenticated(true);
        window.dispatchEvent(new Event('authChange'));
    }, []);

    // Verificar autenticación al montar el componente
    useEffect(() => {
        const checkAuth = () => {
            const token = localStorage.getItem('token');
            const hasToken = !!token;
            
            setIsAuthenticated(hasToken);
            
            if (hasToken && isTokenExpiringSoon()) {
                console.log('🔄 Token próximo a expirar, refrescando...');
                refreshToken();
            }
        };

        checkAuth();
        setLoading(false);

        // Escuchar cambios en la autenticación
        const handleAuthChange = () => {
            checkAuth();
        };

        window.addEventListener('authChange', handleAuthChange);
        return () => window.removeEventListener('authChange', handleAuthChange);
    }, [isTokenExpiringSoon, refreshToken]);

    // Verificar token cada 5 minutos
    useEffect(() => {
        const interval = setInterval(() => {
            if (isAuthenticated && isTokenExpiringSoon()) {
                console.log('🔄 Verificación periódica: Token próximo a expirar');
                refreshToken();
            }
        }, 5 * 60 * 1000); // 5 minutos

        return () => clearInterval(interval);
    }, [isAuthenticated, isTokenExpiringSoon, refreshToken]);

    return {
        isAuthenticated,
        user,
        loading,
        login,
        logout,
        refreshToken,
        isTokenExpiringSoon
    };
};

